import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Compass,
  Eye,
  MoonStar,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { fetchSiteContent } from "@/lib/lumenApi";

const homeVows = ["Voir.", "Comprendre.", "Agir."];
const symbolicMomentImage =
  "https://images.unsplash.com/photo-1533316772091-b8d03d19dce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxkcmFtYXRpYyUyMG1vdW50YWluJTIwbGFrZSUyMHN1bnNldHxlbnwwfHx8YmxhY2t8MTc3NTc4NDQzM3ww&ixlib=rb-4.1.0&q=85";
const finalThresholdImage =
  "https://images.unsplash.com/photo-1704788055201-caae3f50ffe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxjYXRoZWRyYWwlMjBkb29yd2F5JTIwbGlnaHQlMjBzaWxob3VldHRlfGVufDB8fHxibGFja3wxNzc1Nzg1NjEwfDA&ixlib=rb-4.1.0&q=85";
const incarnatedHeroImage =
  "https://static.prod-images.emergentagent.com/jobs/2bcccefa-4614-49bd-b4a9-4611b2eac73f/images/eb58c25e3092777dbc0d9557a05c60b349dca710e40f1a1c6ec78bbcf290d262.png";
const codexNodes = [
  { label: "Voir", x: "12%", y: "64%" },
  { label: "Comprendre", x: "30%", y: "28%" },
  { label: "Relier", x: "48%", y: "62%" },
  { label: "Discernement", x: "62%", y: "22%" },
  { label: "Doctrine", x: "76%", y: "58%" },
  { label: "Agir", x: "88%", y: "34%" },
];

function LoadingState() {
  return (
    <div className="lumen-page">
      <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
        <div className="lumen-frame max-w-xl p-10 text-center">
          <p className="lumen-kicker" data-testid="home-loading-kicker">
            LUMEN
          </p>
          <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="home-loading-title">
            Le sanctuaire s’ouvre lentement.
          </h1>
          <p className="lumen-copy mt-4" data-testid="home-loading-copy">
            Quelques instants suffisent pour laisser apparaître la leçon.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let active = true;

    fetchSiteContent()
      .then((data) => {
        if (active) {
          setSite(data);
        }
      })
      .catch(() => {
        if (active) {
          setErrorMessage("Le seuil reste silencieux pour l’instant. Réessaie dans un moment.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  if (loading || !site) {
    if (!loading && errorMessage) {
      return (
        <div className="lumen-page">
          <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
            <div className="lumen-frame max-w-xl p-10 text-center">
              <p className="lumen-kicker" data-testid="home-error-kicker">
                LUMEN
              </p>
              <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="home-error-title">
                Le sanctuaire n’est pas encore visible.
              </h1>
              <p className="lumen-copy mt-4" data-testid="home-error-copy">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return <LoadingState />;
  }

  const visibleLessons = site.lessons.filter((lesson) => lesson.state === "open");
  const cycleOneLessons = site.lessons.filter((lesson) => lesson.step_label !== "Strate II");
  const stratumLessons = site.lessons.filter((lesson) => lesson.step_label === "Strate II");
  const visibleThinkers = site.library_thinkers.slice(0, 3);
  const visibleLessonsCount = visibleLessons.length;
  const openCycleCount = cycleOneLessons.filter((lesson) => lesson.state === "open").length;
  const previewCycleCount = cycleOneLessons.filter((lesson) => lesson.state === "preview").length;
  const stratumOpenCount = stratumLessons.filter((lesson) => lesson.state === "open").length;
  const featuredVisibleLesson = visibleLessons[visibleLessons.length - 1] ?? site.lessons[0];
  const visibleLessonsTitle =
    stratumOpenCount > 0
      ? "Le corpus vivant actuellement ouvert."
      : "Deux chambres ouvertes. Le reste demeure en aperçu.";
  const sanctumMetrics = [
    { value: String(site.lessons.length), label: "chambres", copy: stratumOpenCount > 0 ? "dans le corpus vivant" : "dans le sanctuaire visible" },
    { value: String(site.library_territories.length), label: "territoires", copy: "pour lire le réel" },
    { value: String(visibleThinkers.length), label: "figures", copy: "dans la constellation" },
    { value: "∞", label: "passages", copy: "intérieurs possibles" },
  ];

  const heroManifest = {
    title: "Le silence devient passage.",
    subtitle:
      "Un lieu rare où la leçon apparaît avant les mots.",
    image: incarnatedHeroImage,
  };

  const renderLessonCard = (lesson, variant = "cycle") => {
    const lessonNumber = site.lessons.findIndex((item) => item.slug === lesson.slug) + 1;
    const cardClassName = lesson.state === "open"
      ? `${variant === "stratum" ? "value-major accent-halo" : "value-access"} threshold-traversal-card`
      : "value-doctrine threshold-orientation-card opacity-80";

    return (
      <Card className={`lumen-card ${cardClassName} h-full`} key={lesson.slug} data-testid={`home-lesson-card-${lesson.slug}`}>
        <CardHeader className="space-y-4">
          <div className="lumen-panel-topline" data-testid={`home-lesson-topline-${lesson.slug}`}>
            <p className="lumen-panel-kicker" data-testid={`home-lesson-kicker-${lesson.slug}`}>
              Leçon {String(lessonNumber).padStart(2, "0")}
            </p>
            <span className="lumen-plus">+</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="lumen-chip" data-testid={`home-lesson-step-${lesson.slug}`}>
              {lesson.step_label}
            </span>
            <span className={`lumen-status-pill ${lesson.state}`} data-testid={`home-lesson-state-${lesson.slug}`}>
              {lesson.state_label}
            </span>
          </div>
          <CardTitle className="lumen-card-title text-3xl" data-testid={`home-lesson-title-${lesson.slug}`}>
            {lesson.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="lumen-copy" data-testid={`home-lesson-excerpt-${lesson.slug}`}>
            {lesson.excerpt}
          </p>
          <Button
            asChild
            className="lumen-gold-button threshold-action-button threshold-traversal-button w-full"
            data-testid={`home-lesson-open-button-${lesson.slug}`}
            variant="default"
          >
            <Link to={`/lecons/${lesson.slug}`}>
              {lesson.state === "open" ? "Ouvrir la chambre" : "Voir l’aperçu"}
              <ArrowRight />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="lumen-page">
      <div className="lumen-shell home-shell">
        <main className="space-y-0 pb-16">
          <section className="lumen-hero-manifest fade-rise delay-1" data-testid="home-hero-section">
            <img
              src={heroManifest.image}
              alt="Scène-manifeste LUMEN avec seuil habité, silhouette contemplative et lumière sacrée"
              className="lumen-hero-manifest-image"
              data-testid="home-hero-image"
            />
            <div className="lumen-hero-manifest-shroud" data-testid="home-hero-shroud" />
            <div className="lumen-hero-manifest-halo" data-testid="home-hero-halo" />
            <div className="lumen-hero-manifest-lightshaft" data-testid="home-hero-lightshaft" />
            <div className="lumen-hero-ritual-frame" data-testid="home-hero-ritual-frame" />
            <div className="lumen-hero-topbar" data-testid="home-header">
              <Link className="lumen-brand lumen-hero-brand" to="/" data-testid="home-brand-link">
                <BrandMark wrapperClassName="lumen-brand-mark" testId="home-brand-sigil" imageTestId="home-brand-sigil-image" />
                <span className="lumen-hero-signature-word" data-testid="home-brand-name">
                  {site.brand_name}
                </span>
              </Link>

              <nav className="lumen-hero-topnav" data-testid="home-header-nav">
                <a className="lumen-nav-link lumen-nav-link-ghost" href="#enseignements" data-testid="home-nav-teachings-link">
                  Enseignements
                </a>
                <Link className="lumen-nav-link lumen-nav-link-bibliotheque" to="/bibliotheque" data-testid="home-nav-library-link">
                  Bibliothèque
                </Link>
                <Link className="lumen-nav-link lumen-nav-link-ghost" to="/entrer" data-testid="home-nav-invitation-link">
                  Seuil d’accès
                </Link>
              </nav>
            </div>

            <div className="lumen-hero-copy" data-testid="home-hero-copy">
              <div className="space-y-5">
                <p className="lumen-kicker lumen-hero-kicker-soft" data-testid="home-hero-kicker">
                  Sanctuaire digital • entrée sélective
                </p>
                <h1 className="lumen-title lumen-hero-title text-5xl sm:text-6xl lg:text-8xl" data-testid="home-hero-title">
                  {heroManifest.title}
                </h1>
                <p className="lumen-copy max-w-2xl text-base md:text-lg" data-testid="home-hero-subtitle">
                  {heroManifest.subtitle}
                </p>
              </div>

              <div className="lumen-hero-actions flex flex-wrap gap-4" data-testid="home-hero-actions">
                <Button asChild className="lumen-gold-button" data-testid="home-primary-entry-button">
                  <Link to="/entrer">
                    Entrer dans LUMEN
                    <ArrowRight />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="lumen-outline-button" data-testid="home-secondary-lesson-button">
                  <Link to="/lecons/effet-miroir">
                    Voir la leçon initiatique
                    <Eye />
                  </Link>
                </Button>
              </div>
            </div>

          </section>

          <section className="lumen-scroll-stage stage-fragment grid gap-6 xl:grid-cols-[0.78fr_1.22fr]" data-testid="home-doctrine-band">
            <div className="lumen-frame value-major space-y-4 p-6" data-testid="home-hero-oracle-panel">
              <p className="lumen-kicker" data-testid="home-oracle-kicker">
                Ligne d’orientation
              </p>
              <p className="text-xl leading-relaxed text-[var(--lumen-ivory)]" data-testid="home-oracle-line">
                {site.oracle_line}
              </p>
            </div>

            <div className="space-y-4" data-testid="home-doctrine-band-right">
              <div className="lumen-metric-grid lumen-subgrid cluster-fragment" data-testid="home-metric-grid">
                {sanctumMetrics.map((metric, index) => (
                  <div className="lumen-metric value-fragment" key={metric.label} data-testid={`home-metric-card-${index + 1}`}>
                    <p className="lumen-metric-value" data-testid={`home-metric-value-${index + 1}`}>
                      {metric.value}
                    </p>
                    <p className="lumen-metric-label" data-testid={`home-metric-label-${index + 1}`}>
                      {metric.label}
                    </p>
                    <p className="lumen-metric-copy" data-testid={`home-metric-copy-${index + 1}`}>
                      {metric.copy}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-3 lumen-subgrid cluster-fragment" data-testid="home-key-lines-grid">
                {site.key_lines.map((line, index) => (
                  <Card className="lumen-card value-fragment h-full" key={line} data-testid={`home-key-line-card-${index + 1}`}>
                    <CardContent className="p-5">
                      <div className="lumen-panel-topline mb-4" data-testid={`home-key-line-topline-${index + 1}`}>
                        <p className="lumen-panel-kicker" data-testid={`home-key-line-kicker-${index + 1}`}>
                          Axiome {String(index + 1).padStart(2, "0")}
                        </p>
                        <span className="lumen-plus">+</span>
                      </div>
                      <div className="mb-3 text-[var(--lumen-gold)]">
                        <Sparkles className="h-4 w-4" />
                      </div>
                      <p className="lumen-copy text-sm" data-testid={`home-key-line-text-${index + 1}`}>
                        {line}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <section id="enseignements" className="lumen-scroll-stage stage-doctrine space-y-8 fade-rise delay-2" data-testid="home-pillars-section">
            <div className="space-y-3">
              <p className="lumen-kicker" data-testid="home-pillars-kicker">
                Ce que protège LUMEN
              </p>
              <div className="lumen-section-heading" data-testid="home-pillars-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="home-pillars-title">
                  Une architecture pensée comme un rite d’approfondissement.
                </h2>
                <span className="lumen-plus" data-testid="home-pillars-plus">
                  +
                </span>
              </div>
              <Separator className="lumen-separator max-w-3xl" data-testid="home-pillars-separator" />
            </div>

            <div className="grid gap-5 lg:grid-cols-3 lumen-subgrid cluster-doctrine" data-testid="home-pillars-grid">
              {site.pillars.map((pillar, index) => (
                <Card className={`lumen-card value-doctrine accent-${pillar.accent} h-full`} key={pillar.id} data-testid={`home-pillar-card-${pillar.id}`}>
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid={`home-pillar-topline-${pillar.id}`}>
                      <p className="lumen-panel-kicker" data-testid={`home-pillar-panel-kicker-${pillar.id}`}>
                        Repère {String(index + 1).padStart(2, "0")}
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="lumen-symbol" data-testid={`home-pillar-symbol-${pillar.id}`}>
                        {pillar.symbol}
                      </span>
                      <Eye className="h-4 w-4 text-[var(--lumen-gold)]" />
                    </div>
                    <CardTitle className="lumen-card-title" data-testid={`home-pillar-title-${pillar.id}`}>
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="lumen-copy" data-testid={`home-pillar-description-${pillar.id}`}>
                      {pillar.description}
                    </p>
                    <p className="lumen-footnote mt-6" data-testid={`home-pillar-footnote-${pillar.id}`}>
                      Fragment doctrinal • {pillar.title}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-doctrine space-y-8 fade-rise delay-3" data-testid="home-domains-section">
            <div className="space-y-3">
              <p className="lumen-kicker" data-testid="home-domains-kicker">
                Les chambres du parcours
              </p>
              <div className="lumen-section-heading" data-testid="home-domains-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="home-domains-title">
                  Chaque section ralentit le regard pour laisser apparaître plus de vérité.
                </h2>
                <span className="lumen-plus" data-testid="home-domains-plus">
                  +
                </span>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-3 lumen-subgrid cluster-doctrine" data-testid="home-domains-grid">
              {site.domains.map((domain, index) => (
                <Card className={`lumen-card value-doctrine accent-${domain.accent} h-full`} key={domain.id} data-testid={`home-domain-card-${domain.id}`}>
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid={`home-domain-topline-${domain.id}`}>
                      <p className="lumen-panel-kicker" data-testid={`home-domain-kicker-${domain.id}`}>
                        Chambre {String(index + 1).padStart(2, "0")}
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="lumen-symbol" data-testid={`home-domain-symbol-${domain.id}`}>
                        {domain.symbol}
                      </span>
                      {domain.id === "teachings" ? <BookOpen className="h-4 w-4 text-[var(--lumen-gold)]" /> : null}
                      {domain.id === "cartographies" ? <Compass className="h-4 w-4 text-[var(--lumen-gold)]" /> : null}
                      {domain.id === "rituals" ? <MoonStar className="h-4 w-4 text-[var(--lumen-gold)]" /> : null}
                    </div>
                    <CardTitle className="lumen-card-title" data-testid={`home-domain-title-${domain.id}`}>
                      {domain.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="lumen-copy" data-testid={`home-domain-description-${domain.id}`}>
                      {domain.description}
                    </p>
                    <p className="lumen-footnote mt-6" data-testid={`home-domain-footnote-${domain.id}`}>
                      Carte de savoir • {domain.title}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-threshold space-y-8 fade-rise delay-3" data-testid="home-lessons-section">
            <div className="space-y-3">
              <p className="lumen-kicker" data-testid="home-lessons-kicker">
                Seuils de traversée
              </p>
              <div className="lumen-section-heading" data-testid="home-lessons-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="home-lessons-title">
                  {visibleLessonsTitle}
                </h2>
                <span className="lumen-plus" data-testid="home-lessons-plus">
                  +
                </span>
              </div>
              <div className="flex flex-wrap gap-4" data-testid="home-lessons-actions">
                <Button asChild variant="outline" className="lumen-outline-button threshold-action-button threshold-reorient-button" data-testid="home-journey-map-button">
                  <Link to="/parcours">
                    Voir la carte du cycle
                    <Compass />
                  </Link>
                </Button>
              </div>
            </div>

            {cycleOneLessons.length > 0 ? (
              <div className="space-y-5" data-testid="home-cycle-one-block">
                <div className="lumen-frame value-fragment space-y-4 p-6" data-testid="home-cycle-one-seal-panel">
                  <p className="lumen-panel-kicker" data-testid="home-cycle-one-seal-kicker">
                    Cycle I
                  </p>
                  <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]" data-testid="home-cycle-one-seal-title">
                    {openCycleCount >= 6
                      ? "Cycle I ouvert."
                      : `${openCycleCount} chambres ouvertes • ${previewCycleCount} aperçus.`}
                  </p>
                </div>

                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 lumen-subgrid cluster-doctrine" data-testid="home-cycle-one-grid">
                  {cycleOneLessons.map((lesson) => renderLessonCard(lesson))}
                </div>
              </div>
            ) : null}

            {stratumLessons.length > 0 ? (
              <div className="space-y-5" data-testid="home-stratum-two-block">
                <div className="lumen-frame value-major space-y-4 p-6" data-testid="home-stratum-two-panel">
                  <p className="lumen-panel-kicker" data-testid="home-stratum-two-kicker">
                    Strate II
                  </p>
                  <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]" data-testid="home-stratum-two-title">
                    Une profondeur plus grave.
                  </p>
                </div>

                <div className="grid gap-5 xl:grid-cols-2 lumen-subgrid cluster-doctrine" data-testid="home-stratum-two-grid">
                  {stratumLessons.map((lesson) => renderLessonCard(lesson, "stratum"))}
                </div>
              </div>
            ) : null}
          </section>

          <section className="lumen-scroll-stage stage-manifest stage-pivot lumen-stage-panel value-major symbolic-stage fade-rise delay-3" data-testid="home-symbolic-stage">
            <img
              src={symbolicMomentImage}
              alt="Paysage crépusculaire sacré avec portail symbolique"
              className="lumen-stage-image"
              data-testid="home-symbolic-stage-image"
            />
            <div className="lumen-stage-overlay" data-testid="home-symbolic-stage-overlay" />
            <div className="lumen-stage-axis" data-testid="home-symbolic-stage-axis" />
            <div className="lumen-stage-portal" data-testid="home-symbolic-stage-portal" />
            <div className="lumen-stage-copy" data-testid="home-symbolic-stage-copy">
              <p className="lumen-kicker" data-testid="home-symbolic-stage-kicker">
                Révélation visuelle
              </p>
              <h3 className="lumen-title text-4xl sm:text-5xl lg:text-6xl" data-testid="home-symbolic-stage-title">
                Un seuil ne se visite pas. Il te recompose.
              </h3>
              <p className="lumen-copy max-w-2xl text-base md:text-lg" data-testid="home-symbolic-stage-text">
                LUMEN n’accumule pas des contenus. Il organise des passages : architecture, vision, doctrine et silence dans une même montée.
              </p>
            </div>
            <div className="lumen-stage-plaque plaque-top-left" data-testid="home-symbolic-stage-plaque-1">
              <p className="lumen-panel-kicker">Seuil 02</p>
              <p className="lumen-image-cartouche-text">Le symbole agit ici comme une porte lente, pas comme un décor.</p>
            </div>
            <div className="lumen-stage-plaque plaque-bottom-right" data-testid="home-symbolic-stage-plaque-2">
              <p className="lumen-panel-kicker">Lecture sacrée</p>
              <p className="lumen-image-cartouche-text">Chaque image doit ressembler à un fragment de mythe lisible dans le silence.</p>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-doctrine lumen-codex-stage value-major fade-rise delay-3" data-testid="home-codex-stage">
            <div className="space-y-5" data-testid="home-codex-copy">
              <p className="lumen-kicker" data-testid="home-codex-kicker">
                Codex vivant
              </p>
              <h3 className="lumen-title text-4xl sm:text-5xl" data-testid="home-codex-title">
                Une grammaire visuelle qui n’appartient qu’à LUMEN.
              </h3>
              <p className="lumen-copy max-w-xl text-base md:text-lg" data-testid="home-codex-text">
                Axes, seuils, fragments, portails, plaques et constellations : la doctrine se lit aussi par la structure. Le scroll devient transmission.
              </p>
            </div>

            <div className="lumen-constellation" data-testid="home-codex-constellation">
              <span className="lumen-constellation-line line-1" />
              <span className="lumen-constellation-line line-2" />
              <span className="lumen-constellation-line line-3" />
              <span className="lumen-constellation-line line-4" />
              <span className="lumen-constellation-line line-5" />
              <span className="lumen-constellation-line line-6" />
              {codexNodes.map((node, index) => (
                <div
                  className="lumen-constellation-node"
                  key={node.label}
                  style={{ left: node.x, top: node.y }}
                  data-testid={`home-codex-node-${index + 1}`}
                >
                  <span className="lumen-constellation-dot" />
                  <span className="lumen-constellation-label">{node.label}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="bibliotheque" className="lumen-scroll-stage stage-manifest stage-pivot home-library-section" data-testid="home-library-section">
            <div className="space-y-4 fade-rise delay-3">
              <p className="lumen-kicker" data-testid="home-library-kicker">
                {site.library_eyebrow}
              </p>
              <div className="lumen-section-heading" data-testid="home-library-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="home-library-title">
                  {site.library_title}
                </h2>
                <span className="lumen-plus" data-testid="home-library-plus">
                  +
                </span>
              </div>
              <p className="lumen-copy max-w-3xl text-base md:text-lg" data-testid="home-library-intro">
                {site.library_intro}
              </p>
            </div>

            <div className="home-library-grid grid gap-6 xl:grid-cols-[1.1fr_0.9fr]" data-testid="home-library-grid">
              <Card className="lumen-card value-major h-full" data-testid="home-library-territories-card">
                <CardHeader className="space-y-4">
                  <div className="lumen-panel-topline" data-testid="home-library-territories-topline">
                    <p className="lumen-panel-kicker" data-testid="home-library-territories-kicker">
                      Cartographie des enseignements
                    </p>
                    <span className="lumen-plus">+</span>
                  </div>
                  <CardTitle className="lumen-card-title text-3xl" data-testid="home-library-territories-title">
                    Les grands territoires de connaissance du sanctuaire.
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="lumen-library-territories" data-testid="home-library-territories-list">
                    {site.library_territories.map((territory) => (
                      <div className="lumen-library-territory" key={territory.id} data-testid={`home-library-territory-${territory.id}`}>
                        <p className="lumen-panel-kicker" data-testid={`home-library-territory-title-${territory.id}`}>
                          {territory.title}
                        </p>
                        <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]" data-testid={`home-library-territory-axis-${territory.id}`}>
                          {territory.axis}
                        </p>
                        <p className="lumen-copy text-sm" data-testid={`home-library-territory-note-${territory.id}`}>
                          {territory.note}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="home-library-thinkers-column space-y-6" data-testid="home-library-thinkers-column">
                <Card className="lumen-card value-doctrine h-full home-library-thinkers-card" data-testid="home-library-thinkers-card">
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid="home-library-thinkers-topline">
                      <p className="lumen-panel-kicker" data-testid="home-library-thinkers-kicker">
                        Constellation tutélaire
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <CardTitle className="lumen-card-title text-3xl" data-testid="home-library-thinkers-title">
                      Des figures de pensée comme sources de lecture du réel.
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="lumen-thinker-list" data-testid="home-library-thinkers-list">
                      {visibleThinkers.map((thinker) => (
                        <div className="lumen-thinker-entry" key={thinker.name} data-testid={`home-library-thinker-${thinker.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          <p className="lumen-panel-kicker">{thinker.territory}</p>
                          <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]">{thinker.name}</p>
                          <p className="lumen-copy text-sm">{thinker.lineage}</p>
                          <p className="lumen-copy text-sm">{thinker.note}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <div className="lumen-frame value-fragment home-library-note-panel space-y-4 p-6" data-testid="home-library-note-panel">
                  <p className="lumen-panel-kicker" data-testid="home-library-note-kicker">
                    Corpus vivant
                  </p>
                  <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]" data-testid="home-library-note-text">
                    {site.library_note}
                  </p>
                  <Link className="lumen-nav-link library-entry-link" to="/bibliotheque" data-testid="home-library-entry-link">
                    /bibliotheque
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section id="invitation" className="lumen-scroll-stage stage-threshold stage-access stage-pivot home-invitation-section grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="home-invitation-section">
            <div className="lumen-stage-panel value-major invitation-stage threshold-request-stage home-invitation-copy-card h-full" data-testid="home-invitation-copy-card">
              <img
                src={finalThresholdImage}
                alt="Silhouette dans un seuil architectural monumental"
                className="lumen-stage-image invitation-stage-image"
                data-testid="home-invitation-stage-image"
              />
              <div className="lumen-stage-overlay" data-testid="home-invitation-stage-overlay" />
              <div className="lumen-stage-axis" data-testid="home-invitation-stage-axis" />
              <div className="lumen-stage-copy" data-testid="home-invitation-stage-copy">
                <p className="lumen-kicker" data-testid="home-invitation-eyebrow">
                  Seuil d’accès
                </p>
                <h3 className="lumen-title text-4xl sm:text-5xl" data-testid="home-invitation-title">
                  Le gratuit révèle ; le passage ouvre réellement.
                </h3>
                <p className="lumen-copy max-w-xl text-base md:text-lg" data-testid="home-invitation-description">
                  Deux seuils réels, un seul lieu.
                </p>
              </div>
              <div className="lumen-stage-plaque plaque-bottom-right" data-testid="home-invitation-note">
                <p className="lumen-panel-kicker">Note de passage</p>
                <p className="lumen-image-cartouche-text">Le 29 € reste le seuil naturel. Le 79 € demeure l’ensemble du corpus vivant actuellement ouvert.</p>
              </div>
            </div>

            <Card className="lumen-card value-access threshold-request-card h-full" data-testid="home-invitation-form-card">
              <CardHeader className="space-y-4">
                <div className="lumen-panel-topline" data-testid="home-form-topline">
                  <p className="lumen-panel-kicker" data-testid="home-form-panel-kicker">
                    Passage réel
                  </p>
                  <span className="lumen-plus">+</span>
                </div>
                <p className="lumen-kicker" data-testid="home-form-kicker">
                  Entrer sans banaliser
                </p>
                <CardTitle className="lumen-card-title text-3xl" data-testid="home-form-title">
                  Deux seuils.
                </CardTitle>
                <p className="threshold-form-support lumen-copy" data-testid="home-form-support">
                  29 € • Cycle I complet. 79 € • Corpus vivant actuellement ouvert.
                </p>
              </CardHeader>
              <CardContent>
                <div className="home-access-brief-grid" data-testid="home-access-brief-grid">
                  <div className="lumen-node value-fragment home-access-brief-card" data-testid="home-access-brief-cycle-card">
                    <p className="lumen-panel-kicker" data-testid="home-access-brief-cycle-kicker">
                      Entrer dans LUMEN — 29 €
                    </p>
                    <p className="lumen-card-title text-2xl" data-testid="home-access-brief-cycle-title">
                      Ouvrir le Cycle I complet.
                    </p>
                    <p className="lumen-copy text-sm" data-testid="home-access-brief-cycle-copy">
                      Le premier dedans réel : six chambres, une seule montée, une continuité habitable.
                    </p>
                  </div>

                  <div className="lumen-node value-fragment home-access-brief-card" data-testid="home-access-brief-integral-card">
                    <p className="lumen-panel-kicker" data-testid="home-access-brief-integral-kicker">
                      LUMEN Intégral — 79 €
                    </p>
                    <p className="lumen-card-title text-2xl" data-testid="home-access-brief-integral-title">
                      Le corpus vivant actuellement ouvert.
                    </p>
                    <p className="lumen-copy text-sm" data-testid="home-access-brief-integral-copy">
                      Cycle I complet, Strate II complète, et la Bibliothèque approfondie selon son état réel.
                    </p>
                  </div>

                  <div className="home-access-brief-actions" data-testid="home-form-actions-row">
                    <Button asChild className="lumen-gold-button threshold-action-button threshold-request-button" data-testid="home-invitation-submit-button">
                      <Link to="/entrer">
                        Franchir le seuil
                        <ArrowRight />
                      </Link>
                    </Button>
                    <Link className="lumen-nav-link threshold-secondary-link inline-flex items-center gap-2" to={`/lecons/${featuredVisibleLesson.slug}`} data-testid="home-direct-lesson-link">
                      Voir une chambre ouverte
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>
      </div>
    </div>
  );
}